import { useCallback, useEffect, useRef, useState } from "react"
import { RgbaColorPicker } from "react-colorful"
import type { RgbaColor } from "react-colorful"
import { ChevronDown, ChevronUp, Pipette } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

const PALETTE = [
  "#f44336",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#2196f3",
  "#03a9f4",
  "#00bcd4",
  "#009688",
  "#4caf50",
  "#8bc34a",
  "#cddc39",
  "#ffeb3b",
  "#ffc107",
  "#ff9800",
  "#ff5722",
]

interface ColorInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  id?: string
  /** When true, only the color swatch (picker trigger) is shown; the text input is hidden. */
  swatchOnly?: boolean
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n))
}

/** Parse various color strings into { r, g, b, a } with a in [0,1]. */
function parseToRgba(val: string): RgbaColor {
  const s = val.trim()
  if (!s) return { r: 51, g: 51, b: 51, a: 1 }
  if (/^transparent$/i.test(s)) return { r: 0, g: 0, b: 0, a: 0 }
  // #rgb
  let m = /^#([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/i.exec(s)
  if (m)
    return {
      r: parseInt(m[1] + m[1], 16),
      g: parseInt(m[2] + m[2], 16),
      b: parseInt(m[3] + m[3], 16),
      a: 1,
    }
  // #rrggbb
  m = /^#?([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/i.exec(s)
  if (m)
    return {
      r: parseInt(m[1], 16),
      g: parseInt(m[2], 16),
      b: parseInt(m[3], 16),
      a: 1,
    }
  // #rrggbbaa or #rgba
  m = /^#?([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/i.exec(s)
  if (m)
    return {
      r: parseInt(m[1], 16),
      g: parseInt(m[2], 16),
      b: parseInt(m[3], 16),
      a: parseInt(m[4], 16) / 255,
    }
  m = /^#?([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/i.exec(s)
  if (m)
    return {
      r: parseInt(m[1] + m[1], 16),
      g: parseInt(m[2] + m[2], 16),
      b: parseInt(m[3] + m[3], 16),
      a: parseInt(m[4] + m[4], 16) / 255,
    }
  // rgba(r,g,b,a) or rgb(r,g,b)
  m = /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i.exec(s)
  if (m)
    return {
      r: clamp(Number(m[1]), 0, 255),
      g: clamp(Number(m[2]), 0, 255),
      b: clamp(Number(m[3]), 0, 255),
      a: m[4] != null ? clamp(Number(m[4]), 0, 1) : 1,
    }
  // hsl(h,s%,l%) or hsla(h,s%,l%,a)
  m = /^hsla?\s*\(\s*([\d.]+)(?:deg)?\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%(?:\s*,\s*([\d.]+))?\s*\)/i.exec(s)
  if (m) {
    const { r, g, b } = hslToRgb(Number(m[1]), Number(m[2]), Number(m[3]))
    return {
      r,
      g,
      b,
      a: m[4] != null ? clamp(Number(m[4]), 0, 1) : 1,
    }
  }
  return { r: 51, g: 51, b: 51, a: 1 }
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
  }
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
  }
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h = 0,
    s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

function rgbaToOutput(r: number, g: number, b: number, a: number): string {
  if (a <= 0) return "transparent"
  if (a >= 1) {
    const pad = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0")
    return "#" + pad(r) + pad(g) + pad(b)
  }
  return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${Math.round(a * 100) / 100})`
}

function rgbaToHex(r: number, g: number, b: number, a: number): string {
  const pad = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0")
  if (a >= 1) return "#" + pad(r) + pad(g) + pad(b)
  return "#" + pad(r) + pad(g) + pad(b) + pad(a * 255)
}

const supportsEyeDropper = typeof window !== "undefined" && "EyeDropper" in window

type InputMode = "hex" | "rgba" | "hsla"

const MODES: { value: InputMode; label: string }[] = [
  { value: "hex", label: "HEX" },
  { value: "rgba", label: "RGBA" },
  { value: "hsla", label: "HSLA" },
]

export function ColorInput({ value, onChange, placeholder, className, id, swatchOnly }: ColorInputProps) {
  const [open, setOpen] = useState(false)
  const [inputMode, setInputMode] = useState<InputMode>("hex")
  const rgba = parseToRgba(value)
  const [r, setR] = useState(rgba.r)
  const [g, setG] = useState(rgba.g)
  const [b, setB] = useState(rgba.b)
  const [a, setA] = useState(rgba.a)
  const [hexInputValue, setHexInputValue] = useState(() => rgbaToHex(rgba.r, rgba.g, rgba.b, rgba.a))
  const paletteRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // While the picker is open, internal state is the source of truth during drag; syncing
    // from value here would overwrite r,g,b,a on every parent re-render and cause an
    // infinite loop (onChange → parent setState → value → this effect → setState → …).
    if (open) return
    const next = parseToRgba(value)
    setR(next.r)
    setG(next.g)
    setB(next.b)
    setA(next.a)
  }, [value, open])

  useEffect(() => {
    setHexInputValue(rgbaToHex(r, g, b, a))
  }, [r, g, b, a])

  const syncRgba = useCallback(
    (ir: number, ig: number, ib: number, ia: number) => {
      setR(ir)
      setG(ig)
      setB(ib)
      setA(ia)
      onChange(rgbaToOutput(ir, ig, ib, ia))
    },
    [onChange]
  )

  const handlePickerChange = useCallback((c: RgbaColor) => syncRgba(c.r, c.g, c.b, c.a), [syncRgba])

  const handleHexChange = useCallback(
    (raw: string) => {
      const c = parseToRgba(raw)
      syncRgba(c.r, c.g, c.b, c.a)
    },
    [syncRgba]
  )

  const handleRgbaChange = useCallback(
    (ir: number, ig: number, ib: number, ia: number) => {
      syncRgba(ir, ig, ib, ia)
    },
    [syncRgba]
  )

  const handleEyedropper = useCallback(() => {
    if (!supportsEyeDropper) return
    ;(new (window as any).EyeDropper().open() as Promise<{ sRGBHex: string }>)
      .then(({ sRGBHex }) => handleHexChange(sRGBHex))
      .catch(() => {})
  }, [handleHexChange])

  const lighten = useCallback(() => {
    const d = 17
    syncRgba(Math.min(255, r + d), Math.min(255, g + d), Math.min(255, b + d), a)
  }, [r, g, b, a, syncRgba])

  const darken = useCallback(() => {
    const d = 17
    syncRgba(Math.max(0, r - d), Math.max(0, g - d), Math.max(0, b - d), a)
  }, [r, g, b, a, syncRgba])

  const hexStr = rgbaToHex(r, g, b, a)
  const hsl = rgbToHsl(r, g, b)

  // Use longhand props only (no `background` shorthand) to avoid React warning when mixing with backgroundSize/backgroundPosition
  const swatchStyle =
    a < 1
      ? {
          backgroundImage: `linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)`,
          backgroundColor: `rgba(${r},${g},${b},${a})`,
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
        }
      : { backgroundColor: `rgb(${r},${g},${b})` }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-6 w-7 shrink-0 rounded border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:focus:ring-gray-500"
            style={swatchStyle}
            aria-label="Open color picker"
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto max-w-[calc(100vw-2rem)] border-gray-200 px-1 py-1.5 dark:border-gray-700"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="color-picker-popover space-y-1.5">
            <RgbaColorPicker color={{ r, g, b, a }} onChange={handlePickerChange} className="mx-auto" />
            <div className="flex items-start gap-1">
              {supportsEyeDropper && (
                <button
                  type="button"
                  onClick={handleEyedropper}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                  title="Pick color from screen"
                >
                  <Pipette className="h-3 w-3" />
                </button>
              )}
              <div
                className="h-6 w-6 shrink-0 rounded-full border border-gray-200 shadow-sm dark:border-gray-700"
                style={swatchStyle}
                aria-hidden
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {inputMode === "hex" && (
                  <div className="flex rounded border border-gray-200 dark:border-gray-700">
                    <Input
                      className="h-6 flex-1 border-0 px-1 font-mono text-[10px] focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder={a < 1 ? "#00000000" : "#000000"}
                      value={hexInputValue}
                      onChange={(e) => {
                        const raw = e.target.value.trim()
                        if (!/^#?[0-9A-Fa-f]{0,8}$/i.test(raw) && raw !== "") return
                        const v = raw ? (raw.startsWith("#") ? raw : "#" + raw) : ""
                        setHexInputValue(v)
                        if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/i.test(v))
                          handleHexChange(v)
                      }}
                      onBlur={() => {
                        if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/i.test(hexInputValue))
                          setHexInputValue(hexStr)
                      }}
                    />
                    <div className="flex flex-col border-l border-gray-200 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={lighten}
                        className="flex flex-1 items-center justify-center p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                        aria-label="Lighten"
                      >
                        <ChevronUp className="h-2 w-2" />
                      </button>
                      <button
                        type="button"
                        onClick={darken}
                        className="flex flex-1 items-center justify-center p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                        aria-label="Darken"
                      >
                        <ChevronDown className="h-2 w-2" />
                      </button>
                    </div>
                  </div>
                )}
                {inputMode === "rgba" && (
                  <div className="flex flex-wrap gap-x-0.5 gap-y-0.5">
                    {[
                      { label: "R", val: r, set: (n: number) => handleRgbaChange(n, g, b, a) },
                      { label: "G", val: g, set: (n: number) => handleRgbaChange(r, n, b, a) },
                      { label: "B", val: b, set: (n: number) => handleRgbaChange(r, g, n, a) },
                      {
                        label: "A",
                        val: a,
                        set: (n: number) => handleRgbaChange(r, g, b, n),
                        min: 0,
                        max: 1,
                        step: 0.01,
                      },
                    ].map(({ label, val, set: setVal, min = 0, max = 255, step = 1 }) => (
                      <div key={label} className="flex w-[38px] flex-col">
                        <Input
                          type="number"
                          min={min}
                          max={max}
                          step={step}
                          className="h-6 px-0.5 text-center text-[10px]"
                          value={label === "A" ? (Math.round(val * 100) / 100).toString() : val}
                          onChange={(e) =>
                            setVal(
                              label === "A"
                                ? clamp(Number(e.target.value) || 0, 0, 1)
                                : clamp(Number(e.target.value) || 0, 0, 255)
                            )
                          }
                        />
                        <span className="text-center text-[9px] font-medium text-gray-500 dark:text-gray-400">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {inputMode === "hsla" && (
                  <div className="flex flex-wrap gap-x-0.5 gap-y-0.5">
                    {[
                      {
                        label: "H",
                        val: hsl.h,
                        set: (n: number) => {
                          const { r: nr, g: ng, b: nb } = hslToRgb(n, hsl.s, hsl.l)
                          handleRgbaChange(nr, ng, nb, a)
                        },
                        max: 360,
                      },
                      {
                        label: "S",
                        val: hsl.s,
                        set: (n: number) => {
                          const { r: nr, g: ng, b: nb } = hslToRgb(hsl.h, n, hsl.l)
                          handleRgbaChange(nr, ng, nb, a)
                        },
                        max: 100,
                      },
                      {
                        label: "L",
                        val: hsl.l,
                        set: (n: number) => {
                          const { r: nr, g: ng, b: nb } = hslToRgb(hsl.h, hsl.s, n)
                          handleRgbaChange(nr, ng, nb, a)
                        },
                        max: 100,
                      },
                      {
                        label: "A",
                        val: a,
                        set: (n: number) => handleRgbaChange(r, g, b, n),
                        min: 0,
                        max: 1,
                        step: 0.01,
                      },
                    ].map(({ label, val, set: setVal, min = 0, max = 255, step = 1 }) => (
                      <div key={label} className="flex w-[38px] flex-col">
                        <Input
                          type="number"
                          min={min}
                          max={max}
                          step={step}
                          className="h-6 px-0.5 text-center text-[10px]"
                          value={label === "A" ? (Math.round(val * 100) / 100).toString() : val}
                          onChange={(e) =>
                            setVal(
                              label === "A"
                                ? clamp(Number(e.target.value) || 0, 0, 1)
                                : clamp(Number(e.target.value) || 0, min, max)
                            )
                          }
                        />
                        <span className="text-center text-[9px] font-medium text-gray-500 dark:text-gray-400">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <Select value={inputMode} onValueChange={(v) => setInputMode(v as InputMode)}>
                  <SelectTrigger className="h-auto min-w-0 border-0 bg-transparent px-0 py-0.5 text-[9px] font-medium text-gray-500 shadow-none hover:bg-transparent focus:ring-0 dark:text-gray-400 [&>span]:line-clamp-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODES.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div ref={paletteRef} className="grid max-h-10 flex-1 grid-cols-8 gap-0.5 overflow-y-auto">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="h-3.5 w-3.5 shrink-0 rounded border border-gray-200 transition hover:scale-110 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
                    style={{ backgroundColor: c }}
                    onClick={() => handleHexChange(c)}
                    aria-label={`Pick ${c}`}
                  />
                ))}
              </div>
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => paletteRef.current?.scrollBy({ top: -16, behavior: "smooth" })}
                  className="p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                  aria-label="Scroll palette up"
                >
                  <ChevronUp className="h-2 w-2" />
                </button>
                <button
                  type="button"
                  onClick={() => paletteRef.current?.scrollBy({ top: 16, behavior: "smooth" })}
                  className="p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                  aria-label="Scroll palette down"
                >
                  <ChevronDown className="h-2 w-2" />
                </button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {!swatchOnly && (
        <div className="min-w-0 flex-1">
          <Input
            id={id}
            className="h-6 w-full text-[10px]"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )}
    </div>
  )
}
